/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors */
import React, { ReactNode } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import Heading from "@atlaskit/heading";
import LightbulbFilledIcon from "@atlaskit/icon/glyph/lightbulb-filled";
import { Box, Grid, Inline, Stack, media, xcss } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import { ProductKeys, Editions } from "@atlassiansox/asc-stargate";

import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint";
import Button from "@/components/wpl/button/src";
import { Picture, PictureProps } from "@/components/wpl/image/src";
import TabGroup from "@/components/wpl/tab-group/src";
import { useLocalizedHref } from "@/libraries/web-framework";

// Styles for the content tab component
const TabContentStyles = xcss({
    minHeight: "fit-content",
    maxWidth: "1200px",
    marginInline: "auto",
    gridTemplateColumns: "1fr",
    [media.above.sm]: {
        gridTemplateColumns: "340px 1fr"
    },
    [media.above.md]: {
        gridTemplateColumns: "400px 1fr"
    }
});

const TabContentLeft = xcss({
    order: "2",
    paddingInline: "space.400",
    paddingBlock: "space.200",
    [media.above.sm]: {
        order: "1"
    },
    [media.above.lg]: {
        paddingInline: "space.0"
    }
});

const TabContentRight = css({
    "order": "1",
    "aspectRatio": "84/83",
    "opacity": "0",
    "animation": "fadeIn 0.3s ease 0.3s forwards",
    "@keyframes fadeIn": {
        from: {
            opacity: "0"
        },
        to: {
            opacity: "1"
        }
    },
    [media.above.sm]: {
        order: "2",
        aspectRatio: "681/400"
    }
});

const TabContentLeftHeadingStyles = xcss({
    fontWeight: "700",
    [media.above.md]: {
        fontSize: "32px",
        lineHeight: "38px"
    }
});

const TabContentListStyles = xcss({
    listStyle: "none",
    paddingInlineStart: "space.0",
    marginBlockStart: "space.0",
    marginBlockEnd: "space.400"
});

const TabContentListItemTextStyles = xcss({
    fontSize: "16px",
    lineHeight: "24px",
    paddingInlineEnd: "space.0",
    [media.above.md]: {
        fontSize: "20px",
        lineHeight: "28px"
    }
});

const TabContentRightPictureStyles = css({
    aspectRatio: "131/128",
    paddingBlockEnd: token("space.200", "16px"),
    img: {
        objectFit: "cover",
        aspectRatio: "131/128",
        [media.above.sm]: {
            aspectRatio: "19/11"
        }
    }
});

const TabContentLeftCtaStyles = css({
    "marginInline": "auto",
    "borderRadius": "24px",
    "paddingInline": `${token("space.300", "20px")} !important`, // This is needed to override the padding-left and padding-right from the Button component. We should update the Button component to set padding-inline instead of padding-left and padding-right.
    "background": token("color.background.brand.bold", "#0C66E4"),
    "minHeight": "48px",
    "marginBlockStart": token("space.200"),
    "fontSize": token("font.size.300", "20px"),
    ":hover": {
        background: token("color.background.brand.boldest", "#123263")
    },
    [media.above.sm]: {
        marginInlineStart: "0"
    }
});

const buttonMessage = {
    id: "experiment-jpd-use-case-hero-tabs.try-now",
    defaultMessage: "Try now"
};

// Component for the content of each tab, has a heading, a picture and a list of items with icons
const HeroTabContent = ({
    heading,
    picture,
    list,
    imageAlt
}: {
    heading: {
        id: string;
        defaultMessage: string;
    };
    list: {
        icon: ReactNode;
        text: {
            id: string;
            defaultMessage: string;
        };
    }[];
    picture: PictureProps;
    imageAlt?: {
        id: string;
        defaultMessage: string;
    };
}) => {
    const { formatMessage } = useIntl();
    const signupLink = useLocalizedHref(
        "/try/cloud/signup?bundle=jira-product-discovery"
    );
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.JIRA_PRODUCT_DISCOVERY,
        edition: Editions.FREE,
        redirectUrl: signupLink
    });
    return (
        <Grid gap="space.500" xcss={TabContentStyles}>
            <Box xcss={TabContentLeft}>
                <Box paddingBlockEnd="space.300">
                    <Heading as="h3" level="h700">
                        <Box as="span" xcss={TabContentLeftHeadingStyles}>
                            {formatMessage(heading)}
                        </Box>
                    </Heading>
                </Box>
                <Stack as="ul" space="space.200" xcss={TabContentListStyles}>
                    {list.map((item) => (
                        <Inline
                            as="li"
                            key={item.text.id}
                            alignBlock="center"
                            space="space.100"
                        >
                            {item.icon}
                            <Box as="span" xcss={TabContentListItemTextStyles}>
                                {formatMessage(item.text)}
                            </Box>
                        </Inline>
                    ))}
                    <Button
                        css={TabContentLeftCtaStyles}
                        onClick={openCrossFlow}
                        url={signupLink}
                        text={formatMessage(buttonMessage)}
                        aria-label={buttonMessage}
                        dataEventProps={{
                            "data-product-key": "jiraProductDiscovery",
                            "data-event-component": "linkButton",
                            "data-event-container": "heroSection",
                            "data-event": "clicked",
                            "data-label": formatMessage(buttonMessage),
                            "data-label-english": buttonMessage.defaultMessage,
                            "data-track-event": heading.defaultMessage
                        }}
                    />
                </Stack>
            </Box>
            <div css={TabContentRight}>
                <Picture
                    {...picture}
                    image={{
                        src: picture.image.src,
                        alt: imageAlt
                            ? formatMessage(imageAlt)
                            : picture.image.alt
                    }}
                    css={TabContentRightPictureStyles}
                />
            </div>
        </Grid>
    );
};

// Content for each tab
const tabs = [
    {
        id: "product-strategy",
        textLabel: "Product strategy",
        dataLabelEnglish: "Product strategy",
        component: (
            <HeroTabContent
                heading={{
                    id: "experiment-jpd-use-case-hero-tabs.product-strategy.heading",
                    defaultMessage: "Product strategy"
                }}
                imageAlt={{
                    id: "experiment-jpd-use-case-hero-tabs.product-strategy.alt",
                    defaultMessage:
                        "Table of five ideas ordered by an impact score out of 100 with columns for the goal it contributes to, votes, insights and an impact rating out of 5."
                }}
                picture={{
                    image: {
                        src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/GALILEO-726-jpd-use-case-on-wac/product-strategy-mobile.webp",
                        alt: ""
                    },
                    sources: [
                        {
                            srcSet: "https://wac-cdn.atlassian.com/misc-assets/webp-images/GALILEO-726-jpd-use-case-on-wac/product-strategy-desktop-85.webp",
                            media: "(min-width: 48rem)",
                            type: "image/webp"
                        }
                    ]
                }}
                list={[
                    {
                        text: {
                            id: "experiment-jpd-use-case-hero-tabs.product-strategy.usp1",
                            defaultMessage:
                                "Gather ideas and insights in one place"
                        },
                        icon: (
                            <LightbulbFilledIcon
                                size="xlarge"
                                primaryColor="#0C66E4"
                                label=""
                            />
                        )
                    },
                    {
                        text: {
                            id: "experiment-jpd-use-case-hero-tabs.product-strategy.usp2",
                            defaultMessage:
                                "Build custom views to refine your thinking"
                        },
                        icon: (
                            <Box
                                as="span"
                                xcss={xcss({
                                    width: "48px",
                                    height: "48px"
                                })}
                            >
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_1835_10794)">
                                        <path
                                            d="M14 9.99976H10L10 37.9998H38V33.9998H14L14 9.99976Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M21 22C23.7614 22 26 19.7614 26 17C26 14.2386 23.7614 12 21 12C18.2386 12 16 14.2386 16 17C16 19.7614 18.2386 22 21 22Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M38 24C38 26.2091 36.2091 28 34 28C31.7909 28 30 26.2091 30 24C30 21.7909 31.7909 20 34 20C36.2091 20 38 21.7909 38 24Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M23 32C24.6569 32 26 30.6569 26 29C26 27.3431 24.6569 26 23 26C21.3431 26 20 27.3431 20 29C20 30.6569 21.3431 32 23 32Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_1835_10794">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: {
                            id: "experiment-jpd-use-case-hero-tabs.product-strategy.usp3",
                            defaultMessage:
                                "Integrate data from third-party sources and other Atlassian products"
                        },
                        icon: (
                            <Box
                                as="span"
                                xcss={xcss({
                                    width: "48px",
                                    height: "48px"
                                })}
                            >
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_2033_23962)">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M26.1213 36.0209C22.2161 39.9261 15.8845 39.9261 11.9792 36.0209C8.07399 32.1156 8.07399 25.784 11.9792 21.8788L15.5148 18.3432L18.3432 21.1716L14.8076 24.7072C12.4645 27.0503 12.4645 30.8493 14.8076 33.1924C17.1508 35.5356 20.9498 35.5356 23.2929 33.1924L26.8284 29.6569L29.6569 32.4853L26.1213 36.0209Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M36.0207 26.1214C39.926 22.2161 39.926 15.8845 36.0207 11.9792C32.1155 8.07399 25.7839 8.07398 21.8786 11.9792L18.3431 15.5148L21.1715 18.3432L24.7071 14.8076C27.0502 12.4645 30.8492 12.4645 33.1923 14.8077C35.5354 17.1508 35.5354 20.9498 33.1923 23.2929L29.6568 26.8285L32.4852 29.6569L36.0207 26.1214Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M30.4644 20.3641L20.4644 30.3641L17.636 27.5357L27.636 17.5356L30.4644 20.3641Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_2033_23962">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    }
                ]}
            />
        )
    },
    {
        id: "prioritization",
        textLabel: "Prioritization",
        dataLabelEnglish: "Prioritization",
        component: (
            <HeroTabContent
                heading={{
                    id: "experiment-jpd-use-case-hero-tabs.prioritization.heading",
                    defaultMessage: "Prioritization"
                }}
                imageAlt={{
                    id: "experiment-jpd-use-case-hero-tabs.prioritization.alt",
                    defaultMessage:
                        "Four ideas are scored by their impact, out of five. The idea with an impact score of five out of five is selected."
                }}
                picture={{
                    image: {
                        src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/GALILEO-726-jpd-use-case-on-wac/prioritization-mobile.webp",
                        alt: ""
                    },
                    sources: [
                        {
                            srcSet: "https://wac-cdn.atlassian.com/misc-assets/webp-images/GALILEO-726-jpd-use-case-on-wac/prioritization-desktop.webp",
                            media: "(min-width: 48rem)",
                            type: "image/webp"
                        }
                    ]
                }}
                list={[
                    {
                        text: {
                            id: "experiment-jpd-use-case-hero-tabs.prioritization.usp1",
                            defaultMessage: "List, sort and categorize ideas"
                        },
                        icon: (
                            <Box
                                as="span"
                                xcss={xcss({
                                    width: "48px",
                                    height: "48px"
                                })}
                            >
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_2126_82079)">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M38 10H24L7 27L21 41L38 24V10ZM30 16C28.8954 16 28 16.8954 28 18C28 19.1046 28.8954 20 30 20C31.1046 20 32 19.1046 32 18C32 16.8954 31.1046 16 30 16Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_2126_82079">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: {
                            id: "experiment-jpd-use-case-hero-tabs.prioritization.usp2",
                            defaultMessage:
                                "Prioritize them with custom fields, formulas, and rules"
                        },
                        icon: (
                            <Box
                                as="span"
                                xcss={xcss({
                                    width: "48px",
                                    height: "48px"
                                })}
                            >
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_2126_82076)">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M8.24268 29.5772L12.4853 25.3345L8.24268 21.0919L11.7782 17.5563H20.2635L26.6274 11.1924H36.5269V21.0919L30.163 27.4558V35.9411L26.6274 39.4767L22.3848 35.234L18.1422 39.4767L14.2565 35.591L20.6204 29.2271L18.4991 27.1058L12.1352 33.4696L8.24268 29.5772ZM31.5772 19.6778C30.6009 20.6541 29.018 20.6541 28.0416 19.6778C27.0653 18.7015 27.0653 17.1186 28.0416 16.1423C29.018 15.166 30.6009 15.166 31.5772 16.1423C32.5535 17.1186 32.5535 18.7015 31.5772 19.6778Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_2126_82076">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: {
                            id: "experiment-jpd-use-case-hero-tabs.prioritization.usp3",
                            defaultMessage:
                                "Visualize your priorities with flexible views"
                        },
                        icon: (
                            <Box
                                as="span"
                                xcss={xcss({
                                    width: "48px",
                                    height: "48px"
                                })}
                            >
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_2126_82073)">
                                        <path
                                            d="M14 12H8V36H14V12Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M17 12V18.5H40V12H17Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M40 21.5H17V36H40V21.5Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_2126_82073">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    }
                ]}
            />
        )
    },
    {
        id: "roadmapping",
        textLabel: "Roadmapping",
        dataLabelEnglish: "Roadmapping",
        component: (
            <HeroTabContent
                heading={{
                    id: "experiment-jpd-use-case-hero-tabs.roadmapping.heading",
                    defaultMessage: "Roadmapping"
                }}
                imageAlt={{
                    id: "experiment-jpd-use-case-hero-tabs.roadmapping.alt",
                    defaultMessage:
                        "Ideas are listed on a board with three columns called now, next and later. The ideas in now include how much work is done and in progress."
                }}
                picture={{
                    image: {
                        src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/GALILEO-726-jpd-use-case-on-wac/roadmapping-mobile.webp",
                        alt: ""
                    },
                    sources: [
                        {
                            srcSet: "https://wac-cdn.atlassian.com/misc-assets/webp-images/GALILEO-726-jpd-use-case-on-wac/roadmap-desktop.webp",
                            media: "(min-width: 48rem)",
                            type: "image/webp"
                        }
                    ]
                }}
                list={[
                    {
                        text: {
                            id: "experiment-jpd-use-case-hero-tabs.roadmapping.usp1",
                            defaultMessage:
                                "Share tailored roadmaps, even outside your organization"
                        },
                        icon: (
                            <Box
                                as="span"
                                xcss={xcss({ width: "48px", height: "48px" })}
                            >
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_1846_89689)">
                                        <path
                                            d="M10 14C10 11.2386 12.2386 9 15 9C17.7614 9 20 11.2386 20 14C20 14.2504 19.9816 14.4965 19.9461 14.737L31.4434 20.4857C32.3498 19.5684 33.6085 19 35 19C37.7614 19 40 21.2386 40 24C40 26.7614 37.7614 29 35 29C33.6085 29 32.3498 28.4316 31.4434 27.5143L19.9461 33.263C19.9816 33.5035 20 33.7496 20 34C20 36.7614 17.7614 39 15 39C12.2386 39 10 36.7614 10 34C10 31.2386 12.2386 29 15 29C16.04 29 17.0059 29.3175 17.8059 29.861L29.5278 24L17.8059 18.1391C17.0058 18.6825 16.04 19 15 19C12.2386 19 10 16.7614 10 14Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_1846_89689">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: {
                            id: "experiment-jpd-use-case-hero-tabs.roadmapping.usp2",
                            defaultMessage:
                                "Get feedback with comments, votes, and reactions"
                        },
                        icon: (
                            <Box
                                as="span"
                                xcss={xcss({ width: "48px", height: "48px" })}
                            >
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_1846_89692)">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M20.0005 33.9994L38.0005 33.999V9.99902L10.0005 9.99941L10.0005 39.999L20.0005 33.9994ZM32 20H16V16H32V20ZM25 28H16V24H25V28Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_1846_89692">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: {
                            id: "experiment-jpd-use-case-hero-tabs.roadmapping.usp3",
                            defaultMessage:
                                "Connect ideas to delivery work in Jira"
                        },
                        icon: (
                            <Box
                                as="span"
                                xcss={xcss({ width: "48px", height: "48px" })}
                            >
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_1846_89695)">
                                        <path
                                            d="M16 23C16 18.5778 19.5763 15 23.9792 15C28.3821 15 31.9584 18.5778 31.9584 23H35.9584C35.9584 16.3765 30.5991 11 23.9792 11C17.3594 11 12 16.3765 12 23C12 26.0723 13.1531 28.8764 15.0503 31H9V35H23.9511C23.9604 35 23.9698 35 23.9792 35H31.1825L28.5888 37.5828L31.4113 40.4171L38.852 33.0077L31.4128 25.5842L28.5874 28.4156L31.1772 31L23.9563 31C19.5639 30.9876 16 27.4145 16 23Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_1846_89695">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    }
                ]}
            />
        )
    },
    {
        id: "engineering-management",
        textLabel: "Engineering management",
        dataLabelEnglish: "Engineering management",
        component: (
            <HeroTabContent
                heading={{
                    id: "experiment-jpd-use-case-hero-tabs.product-strategy.heading",
                    defaultMessage: "Engineering management"
                }}
                imageAlt={{
                    id: "experiment-jpd-use-case-hero-tabs.engineering-management.alt",
                    defaultMessage:
                        "List of three ideas allocated to team a or team b. Each idea has a delivery month, status and progress status"
                }}
                picture={{
                    image: {
                        src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/GALILEO-726-jpd-use-case-on-wac/visual-mobile.webp",
                        alt: ""
                    },
                    sources: [
                        {
                            srcSet: "https://wac-cdn.atlassian.com/misc-assets/webp-images/GALILEO-726-jpd-use-case-on-wac/engineering-management-desktop.webp",
                            media: "(min-width: 48rem)",
                            type: "image/webp"
                        }
                    ]
                }}
                list={[
                    {
                        text: {
                            id: "experiment-jpd-use-case-hero-tabs.engineering-management.usp1",
                            defaultMessage: "Plan your team's weekly work"
                        },
                        icon: (
                            <Box
                                as="span"
                                xcss={xcss({ width: "48px", height: "48px" })}
                            >
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_1802_4298)">
                                        <path
                                            d="M8 14.5L30 14.5V9.5L8 9.5V14.5Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M10 30.5L32 30.5V25.5L10 25.5L10 30.5Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M40 38.5L18 38.5V33.5L40 33.5V38.5Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M16 22.5L38 22.5V17.5L16 17.5V22.5Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_1802_4298">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: {
                            id: "experiment-jpd-use-case-hero-tabs.engineering-management.usp2",
                            defaultMessage:
                                "See your capacity needs for individual projects"
                        },
                        icon: (
                            <Box
                                as="span"
                                xcss={xcss({ width: "48px", height: "48px" })}
                            >
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_1802_4301)">
                                        <path
                                            d="M17 31C17 27.134 20.134 24 24 24C27.866 24 31 27.134 31 31V40H17V31Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M30 15C30 18.3137 27.3137 21 24 21C20.6863 21 18 18.3137 18 15C18 11.6863 20.6863 9 24 9C27.3137 9 30 11.6863 30 15Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M30.8118 20.8824C31.1959 20.9595 31.5932 21 32 21C35.3137 21 38 18.3137 38 15C38 11.6863 35.3137 9 32 9C31.5932 9 31.1959 9.04048 30.8118 9.11764C32.1753 10.6952 33 12.7513 33 15C33 17.2487 32.1753 19.3048 30.8118 20.8824Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M17.1882 9.11764C15.8247 10.6952 15 12.7513 15 15C15 17.2487 15.8247 19.3048 17.1882 20.8824C16.8041 20.9595 16.4068 21 16 21C12.6863 21 10 18.3137 10 15C10 11.6863 12.6863 9 16 9C16.4068 9 16.8041 9.04048 17.1882 9.11764Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M16.8586 24C15.0903 25.8037 14 28.2745 14 31V40H10V29C10 26.2386 12.2386 24 15 24H16.8586Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M34 40V31C34 28.2745 32.9097 25.8037 31.1414 24H33C35.7614 24 38 26.2386 38 29V40H34Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_1802_4301">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    },
                    {
                        text: {
                            id: "experiment-jpd-use-case-hero-tabs.engineering-management.usp3",
                            defaultMessage:
                                "Get an executive-level view of work from your engineering team"
                        },
                        icon: (
                            <Box
                                as="span"
                                xcss={xcss({ width: "48px", height: "48px" })}
                            >
                                <svg
                                    width="48"
                                    height="48"
                                    viewBox="0 0 48 48"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g clipPath="url(#clip0_2097_7323)">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M10 10H32V32H10V10ZM21 13L22.7961 18.5279H28.6085L23.9062 21.9443L25.7023 27.4721L21 24.0557L16.2977 27.4721L18.0938 21.9443L13.3915 18.5279H19.2039L21 13Z"
                                            fill="#1868DB"
                                        />
                                        <path
                                            d="M35 19V35H19V39H39V19H35Z"
                                            fill="#1868DB"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_2097_7323">
                                            <rect
                                                width="48"
                                                height="48"
                                                fill="white"
                                            />
                                        </clipPath>
                                    </defs>
                                </svg>
                            </Box>
                        )
                    }
                ]}
            />
        )
    }
];

const useTabs = () => {
    return React.useMemo(() => tabs, []);
};

// Here's the overrides that we need to make the component work with the new design.

const HeroTabStyles = css({
    ".select-container": {
        display: "none"
    },
    "div[role=tablist]": {
        display: "flex",
        overflow: "auto"
    },
    "div[role=tablist]::-webkit-scrollbar": {
        display: "none"
    },
    "div[role=tablist] .tab-group-inner": {
        flexWrap: "nowrap",
        whiteSpace: "nowrap",
        justifyContent: "flex-start",
        marginBlockEnd: token("space.400", "32px"),
        [media.above.sm]: {
            justifyContent: "center"
        }
    },
    "div[role=tablist] .tab-group-inner button": {
        fontSize: "16px",
        lineHeight: "24px",
        [media.above.md]: {
            fontSize: "20px",
            lineHeight: "28px"
        }
    },
    "div[role=tablist] .tab-group-inner button:last-child": {
        marginInlineEnd: token("space.200", "16px"),
        [media.above.sm]: {
            marginInlineEnd: "0"
        }
    },
    "div[role=tablist] .tab-group-inner button[aria-selected=true]": {
        borderColor: token("color.border.selected"),
        color: token("color.border.selected"),
        backgroundColor: token("color.background.selected")
    }
});

const HeroTabs = () => {
    const tabs = useTabs();
    return (
        <div css={HeroTabStyles}>
            <TabGroup
                tabContentType="custom"
                id="experiment-jpd-use-case-hero-tabs"
                dataProductKey="jiraProductDiscovery"
                label="Product tour"
                tabs={tabs}
            />
        </div>
    );
};

export default HeroTabs;
